// Application route constants
// All frontend routes centralized for consistency and maintainability

export const ROUTES = {
  // Public marketing routes
  HOME: "/home",
  ABOUT: "/about",
  CONTACT: "/contact",
  PRIVACY_POLICY: "/privacy-policy",
  TERMS_AND_CONDITIONS: "/terms-and-conditions",

  // Public routes
  ROOT: "/",
  LOGIN: "/login",
  REGISTER: "/register",
  VERIFY_EMAIL: "/verify-email",
  FORGOT_PASSWORD: "/forgot-password",
  RESET_PASSWORD: "/reset-password",

  // Main dashboard
  DASHBOARD: "/dashboard",

  // User management
  PROFILE: "/profile",
  CHANGE_PASSWORD: "/change-password",
  ACTIVATE_DEACTIVATE_USERS: "/activate-deactivate-users",
  PROMOTE_USER_TO_MODERATOR: "/promote-user-to-moderator",

  // Business
  BUSINESS_PLAN: "/business-plan",

  // Wallet
  WALLET: "/wallet",

  // Commissions
  COMMISSIONS: {
    ROOT: "/commissions",
    MY: "/commissions/my",
    ALL: "/commissions/all",
    TEAM: "/commissions/team",
    ANALYTICS: "/commissions/analytics",
    PAYMENT_HISTORY: "/commissions/payment-history",
    USER_DETAILS: "/admin/commissions/user/:userId",
  },

  // Referrals
  REFERRALS: {
    MANUAL_APPROVAL: "/manual-approval",
    MY_WITHDRAWALS: "/my-withdrawals",
    ADMIN: {
      CONFIG: "/admin/referrals/config",
      ANALYTICS: "/admin/referrals/analytics",
      WITHDRAWALS: "/admin/referrals/withdrawals",
      USER_NETWORK: "/admin/referrals/user-network",
    },
    TEAM_NETWORK: "/family",
    FAMILY: "/family",
  },

  // Orders
  ORDERS: {
    ROOT: "/orders",
    PLACE: "/place-order",
    MY: "/my-orders",
    REGISTRATION: "/registration-orders",
    NORMAL: "/normal-orders",
    DELETED: "/deleted-orders",
    REFUND_REQUESTS: "/refund-requests",
  },

  // Donations
  DONATIONS: {
    USER: "/donations",
    ADMIN: "/admin-donations",
  },

  // Emergency withdrawals
  EMERGENCY: {
    USER: "/emergency-withdrawals",
    ADMIN: "/admin-emergency-withdrawals",
  },

  // Products
  PRODUCTS: {
    MANAGE: "/products",
    ADD: "/add-product",
    PUBLIC_SHOP: "/shop",
  },

  // Categories
  CATEGORIES: {
    MANAGE: "/categories",
  },

  // Shipping
  SHIPPING: {
    ZONES: "/shipping/zones",
  },

  // Admin
  DISPATCH_MANAGEMENT: "/dispatch-management",
  SYSTEM_CONFIG: "/system-config",
  MANAGE_PAYMENTS: "/manage-payments",
  ADMIN_ANALYTICS: "/admin-analytics",
  TEAM_ANALYTICS: "/team-analytics",
  LOGS: "/logs",
  PUBLIC_CONTENT: "/admin/public-content",

  // KYC
  KYC: {
    USER: "/kyc",
    ADMIN: "/admin/kyc",
  },

  // Returns & Refunds
  RETURNS: {
    USER: "/returns",
    ADMIN: "/admin/returns",
  },

  // Invoices
  INVOICES: {
    USER: "/invoices",
  },

  // Tax Configuration (Admin)
  TAX_CONFIG: "/admin/tax-config",

  // Commission Bonuses
  COMMISSION_BONUSES: {
    USER: "/my-bonuses",
    ADMIN: "/admin/bonuses",
  },

  // Notifications
  NOTIFICATIONS: "/notifications",

  // Support Tickets
  SUPPORT: {
    USER: "/support",
    ADMIN: "/admin/support",
  },
} as const;

// Helper function to get all public routes
export const getPublicRoutes = (): string[] => {
  return [
    ROUTES.HOME,
    ROUTES.ABOUT,
    ROUTES.CONTACT,
    ROUTES.PRODUCTS.PUBLIC_SHOP,
    ROUTES.PRIVACY_POLICY,
    ROUTES.TERMS_AND_CONDITIONS,
    ROUTES.LOGIN,
    ROUTES.REGISTER,
    ROUTES.VERIFY_EMAIL,
    ROUTES.FORGOT_PASSWORD,
    ROUTES.RESET_PASSWORD,
  ];
};

// Helper function to get all admin routes
export const getAdminRoutes = (): string[] => {
  return [
    ROUTES.BUSINESS_PLAN,
    ROUTES.ACTIVATE_DEACTIVATE_USERS,
    ROUTES.PROMOTE_USER_TO_MODERATOR,
    ROUTES.COMMISSIONS.ALL,
    ROUTES.COMMISSIONS.TEAM,
    ROUTES.COMMISSIONS.ANALYTICS,
    ROUTES.REFERRALS.MANUAL_APPROVAL,
    ROUTES.REFERRALS.ADMIN.CONFIG,
    ROUTES.REFERRALS.ADMIN.ANALYTICS,
    ROUTES.REFERRALS.ADMIN.WITHDRAWALS,
    ROUTES.REFERRALS.ADMIN.USER_NETWORK,
    ROUTES.ORDERS.REGISTRATION,
    ROUTES.ORDERS.NORMAL,
    ROUTES.ORDERS.DELETED,
    ROUTES.ORDERS.REFUND_REQUESTS,
    ROUTES.DONATIONS.ADMIN,
    ROUTES.EMERGENCY.ADMIN,
    ROUTES.PRODUCTS.MANAGE,
    ROUTES.PRODUCTS.ADD,
    ROUTES.CATEGORIES.MANAGE,
    ROUTES.SHIPPING.ZONES,
    ROUTES.DISPATCH_MANAGEMENT,
    ROUTES.SYSTEM_CONFIG,
    ROUTES.MANAGE_PAYMENTS,
    ROUTES.ADMIN_ANALYTICS,
    ROUTES.TEAM_ANALYTICS,
    ROUTES.LOGS,
  ];
};

// Helper function to get all user routes
export const getUserRoutes = (): string[] => {
  return [
    ROUTES.DASHBOARD,
    ROUTES.WALLET,
    ROUTES.PROFILE,
    ROUTES.CHANGE_PASSWORD,
    ROUTES.COMMISSIONS.MY,
    ROUTES.REFERRALS.TEAM_NETWORK,
    ROUTES.ORDERS.ROOT,
    ROUTES.ORDERS.MY,
    ROUTES.DONATIONS.USER,
    ROUTES.EMERGENCY.USER,
  ];
};

// Type definitions for route validation
export type RouteKey = keyof typeof ROUTES;
export type PublicRoute =
  | typeof ROUTES.LOGIN
  | typeof ROUTES.REGISTER
  | typeof ROUTES.VERIFY_EMAIL;
export type ProtectedRoute = Exclude<string, PublicRoute>;
